import React, { useState } from "react";
import { Modal, Spin, Typography, Card, Row, Col, Badge, Empty, Button, Tag, Divider } from "antd";
import { useQuery } from "react-query";
import { getProductOrders, getTables } from "../../../service";
import styled from "styled-components";
import { colors } from "../../../styles/theme";
import { OrderDetailsModal } from "../order-details-modal/OrderDetailsModal";
import dayjs from "dayjs";
import { StyledModal } from "../../organisms/orders-list/OrdersList";
import { useResponsive } from "../../../hooks/useResponsive";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

const { Title, Text } = Typography;

const TableCard = styled(Card) <{ $isActive: boolean }>`
  height: 100%;
  border-radius: 8px;
  border-color: ${props => props.$isActive ? colors.primary : colors.border};
  background-color: ${props => props.$isActive ? colors.white : "#f9f9f9"};
  transition: all 0.3s ease;

  .ant-card-head {
    background-color: ${props => props.$isActive ? colors.primaryLight : 'transparent'};
    border-bottom-color: ${props => props.$isActive ? colors.primary : colors.border};
    min-height: 48px;
  }
  
  .ant-card-body {
    padding: 12px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
`;

interface TablesOrdersModalProps {
    visible: boolean;
    onClose: () => void;
    selectedBranch: any;
}

export const TablesOrdersModal: React.FC<TablesOrdersModalProps> = ({
    visible,
    onClose,
    selectedBranch,
}) => {
    const { t } = useTranslation();
    const { currentBranch } = useBranchContext();
    const curSymbol = currencySymbol(currentBranch?.currency);
    const [selectedOrderId, setSelectedOrderId] = useState<any>(null);
    const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
    const { isMobile } = useResponsive();

    // Fetch tables
    const { data: tables, isLoading: tablesLoading } = useQuery(
        ["tables", selectedBranch?.id],
        () => getTables(selectedBranch?.id),
        {
            enabled: !!selectedBranch && visible,
        }
    );

    // Fetch active orders (IN_QUEUE, IN_PROGRESS, IN_DELIVERY)
    // We need to make parallel requests
    const { data: activeOrders, isLoading: ordersLoading, refetch } = useQuery(
        ["active-orders-tables", selectedBranch?.id],
        async () => {
            if (!selectedBranch?.id) return [];

            const statuses = ["IN_QUEUE", "IN_PROGRESS", "IN_DELIVERY"];
            const promises = statuses.map(status =>
                getProductOrders({ id: selectedBranch.id, status, filters: {} })
            );

            const results = await Promise.all(promises);
            return results.flat();
        },
        {
            enabled: !!selectedBranch && visible,
            refetchInterval: 30000, // Refresh every 30s when visible
        }
    );

    const isLoading = tablesLoading || ordersLoading;

    // Group orders by tableId
    const ordersByTable = React.useMemo(() => {
        if (!activeOrders || !tables) return {};

        const grouped: Record<string, any[]> = {};

        // Initialize with empty arrays for all tables
        tables.forEach((table: any) => {
            grouped[table.id] = [];
        });

        // Distribute orders
        activeOrders.forEach((order: any) => {
            // Check if order is for a table (deliveryType === 'TO_THE_TABLE')
            // AND has a tableId
            if (order.details?.deliveryType === 'TO_THE_TABLE' && order.details?.tableId) {
                if (grouped[order.details.tableId]) {
                    grouped[order.details.tableId].push(order);
                }
            }
        });

        return grouped;
    }, [activeOrders, tables]);

    const handleOrderClick = (order: any) => {
        setSelectedOrderId(order);
        setOrderDetailsVisible(true);
    };

    return (
        <Modal
            title={<Title level={4} style={{ margin: 0 }}>{t("tablesOrdersModal.title")}</Title>}
            open={visible}
            onCancel={onClose}
            width={1200}
            footer={null}
            style={{ top: 20 }}
            styles={{ body: { padding: '24px', maxHeight: '80vh', overflowY: 'auto' } }}
        >
            {isLoading && !tables ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Row gutter={[16, 16]}>
                    {tables?.map((table: any) => {
                        const tableOrders = ordersByTable[table.id] || [];
                        const isActive = tableOrders.length > 0;
                        const totalAmount = tableOrders.reduce((sum: number, order: any) => sum + order.price, 0);

                        return (
                            <Col xs={24} sm={12} md={8} lg={6} key={table.id}>
                                <TableCard
                                    title={<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <span>{table.name}</span>
                                        {isActive && <Badge count={tableOrders.length} style={{ backgroundColor: colors.primary }} />}
                                    </div>}
                                    bordered={true}
                                    $isActive={isActive}
                                >
                                    {isActive ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {tableOrders.map((order: any) => (
                                                <div
                                                    key={order.id}
                                                    style={{
                                                        padding: '8px',
                                                        border: `1px solid ${colors.border}`,
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        backgroundColor: colors.white,
                                                        transition: 'border-color 0.2s'
                                                    }}
                                                    onClick={() => handleOrderClick(order)}
                                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.primary}
                                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <Text strong>#{order.id}</Text>
                                                        <Text>{(order.price / 100).toFixed(2)} {curSymbol}</Text>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Tag color={getStatusColor(order.status)} style={{ margin: 0, fontSize: '10px', lineHeight: '18px' }}>
                                                            {(() => {
                                                                const labelKey = getStatusLabelKey(order.status);
                                                                return labelKey ? t(labelKey) : order.status;
                                                            })()}
                                                        </Tag>
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                                            {dayjs(order.createdAt).format('HH:mm')}
                                                        </Text>
                                                    </div>
                                                </div>
                                            ))}
                                            <Divider style={{ margin: '8px 0' }} />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                                <span>{t("tablesOrdersModal.total")}</span>
                                                <span>{(totalAmount / 100).toFixed(2)} {curSymbol}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px 0', color: colors.textSecondary }}>
                                            {t("tablesOrdersModal.free")}
                                        </div>
                                    )}
                                </TableCard>
                            </Col>
                        );
                    })}

                    {(!tables || tables.length === 0) && (
                        <Col span={24}>
                            <Empty description={t("tablesOrdersModal.noTables")} />
                        </Col>
                    )}
                </Row>
            )}

            {selectedOrderId && (
                <StyledModal
                    title={null}
                    open={orderDetailsVisible}
                    width={isMobile ? "100%" : 900}
                    style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', height: '100vh' } : { top: 10 }}
                    onCancel={(e: any) => {
                        setOrderDetailsVisible(false);
                        setSelectedOrderId(null);
                    }}
                    footer={null}
                    destroyOnClose
                >
                    <OrderDetailsModal
                        id={selectedOrderId.id}
                        status={selectedOrderId.status}
                        onClose={() => {
                            setOrderDetailsVisible(false);
                            setSelectedOrderId(null);
                            refetch();
                        }}
                    />
                </StyledModal>
            )}
        </Modal>
    );
};

const getStatusColor = (status: string) => {
    switch (status) {
        case 'IN_QUEUE': return 'gold';
        case 'IN_PROGRESS': return 'blue';
        case 'IN_DELIVERY': return 'green';
        default: return 'default';
    }
}

const getStatusLabelKey = (status: string): string | null => {
    switch (status) {
        case 'IN_QUEUE': return 'tablesOrdersModal.statusInQueue';
        case 'IN_PROGRESS': return 'tablesOrdersModal.statusInProgress';
        case 'IN_DELIVERY': return 'tablesOrdersModal.statusInDelivery';
        default: return null;
    }
}
